import React, { useRef, useEffect } from 'react';
import { Search, X } from 'lucide-react';
import { useGlobalSearch } from './useGlobalSearch';
import SearchAutocomplete from './SearchAutocomplete';

interface GlobalSearchModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const GlobalSearchModal: React.FC<GlobalSearchModalProps> = ({ isOpen, onClose }) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const {
    query,
    setQuery,
    results,
    isLoading,
    setIsOpen: setAutocompleteOpen,
    selectedIndex,
    handleKeydown,
    selectResult,
    clearSearch,
  } = useGlobalSearch();

  // Focus input when modal opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Handle escape key to close modal
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  // Clear search when modal closes
  useEffect(() => {
    if (!isOpen) {
      clearSearch();
      setAutocompleteOpen(false);
    }
  }, [isOpen, clearSearch, setAutocompleteOpen]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value);
  };

  const handleSmartClose = () => {
    if (query) {
      // If there's text, clear it and keep modal open
      clearSearch();
      if (inputRef.current) {
        inputRef.current.focus();
      }
    } else {
      // If no text, close the modal
      onClose();
    }
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-20 px-4">
      {/* Glassmorphism backdrop */}
      <div
        className="absolute inset-0 bg-black/20 backdrop-blur-sm"
        onClick={handleBackdropClick}
      />

      {/* Modal container */}
      <div className="relative w-full max-w-2xl">
        {/* Search input container */}
        <div className="bg-white/95 backdrop-blur-md border border-gray-200/50 rounded-2xl shadow-2xl overflow-hidden">
          <div className="flex items-center px-6 py-4">
            {/* Search icon */}
            <Search className="w-5 h-5 text-gray-400 mr-3 flex-shrink-0" />

            {/* Search input */}
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={handleInputChange}
              onKeyDown={handleKeydown}
              placeholder="Search artists and aligned contributors..."
              className="flex-1 text-lg bg-transparent border-none outline-none placeholder-gray-400"
            />

            {/* Smart close/clear button */}
            <button
              onClick={handleSmartClose}
              className="p-1 text-gray-400 hover:text-gray-600 transition-colors duration-200 ml-3 flex-shrink-0"
              aria-label={query ? "Clear search" : "Close search"}
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Results container */}
          {query.length >= 2 && (
            <div className="border-t border-gray-100">
              <SearchAutocomplete
                isOpen={true}
                results={results}
                isLoading={isLoading}
                selectedIndex={selectedIndex}
                onSelect={(result) => {
                  selectResult(result);
                  onClose();
                }}
                query={query}
                className="max-h-96 overflow-y-auto"
              />
            </div>
          )}
        </div>

        {/* Keyboard shortcuts hint - hidden on mobile */}
        <div className="hidden sm:flex items-center justify-center mt-6 text-sm text-gray-500">
          <span className="bg-white/80 backdrop-blur-sm px-3 py-1 rounded-lg border border-gray-200/50">
            Press <kbd className="px-1.5 py-0.5 bg-gray-100 rounded text-xs font-mono">ESC</kbd> to close
          </span>
        </div>
      </div>
    </div>
  );
};

export default GlobalSearchModal;